import {
  addDays,
  eachDayOfInterval,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { isBetween } from 'shared/lib/date-fns';
import {
  FrequencyUnit,
  HabitSchedule,
  PlannedOccurrence,
  ScheduleEntryState,
} from 'shared/types/habit-schedule';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { v4 as uuidv4 } from 'uuid';

import { getScheduleFrequency } from '../get-schedule-frequency';
import { getActualScheduleStartDate } from './get-actual-schedule-start-date';
import { getScheduleEndDate } from './get-schedule-end-date';
import { getScheduleTimeframesByDateRange } from './get-schedule-timeframe-by-date';

const dateFormat = 'yyyy-MM-dd';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
};

export const getFrequencyPlanningBySchedule = (
  schedule: HabitSchedule,
  {
    startDate,
    endDate,
    weekStartsOn,
    uncompletedOnly,
    sinceLastEntryOnly,
  }: Options,
) => {
  // figure out where to start from
  const scheduleStartDate = startOfDay(schedule.startDate);
  const scheduleStart = getActualScheduleStartDate(schedule);
  const scheduleEnd = getScheduleEndDate(schedule);

  const _start = new Date(
    Math.max(
      // get the later date of the two; given startDate or schedule start date
      startDate.getTime(),
      scheduleStart.getTime(),
    ),
  );

  // figure out where to end
  const _end = new Date(
    Math.min(endDate.getTime(), scheduleEnd?.getTime() ?? endDate.getTime()),
  );

  // get the timeframes based on the start, end and frequency
  const frequency = getScheduleFrequency(schedule);
  const count = schedule.frequency?.count ?? 0;

  // month and year schedules we only create entries for when we are sure there are more than the amount of days in that period
  if (
    (frequency === FrequencyUnit.Month && count < 31) ||
    (frequency === FrequencyUnit.Year && count < 365)
  ) {
    // when there are plannedOccurrences, we show them
    return (
      schedule.plannedOccurrences?.filter(({ date }) => {
        const dateEntry = schedule.entries.find(({ date: entryDate }) =>
          isSameDay(date, entryDate),
        );
        return (
          dateEntry?.state !== ScheduleEntryState.Skip &&
          isBetween(date, _start, _end)
        );
      }) ?? []
    );
  }

  const { skippedDates, completedDates, lastEntryDate } =
    schedule.entries.reduce<{
      skippedDates: Set<string>;
      completedDates: Set<string>;
      lastEntryDate?: Timestamp;
    }>(
      (acc, entry) => {
        // get the skipped dates to exclude them from the plannedOccurrences
        if (entry.state === ScheduleEntryState.Skip) {
          acc.skippedDates.add(format(entry.date, dateFormat));
        }

        // get the completed dates to exclude them from the plannedOccurrences
        if (
          entry.state === ScheduleEntryState.Complete &&
          !!entry.value &&
          uncompletedOnly
        ) {
          acc.completedDates.add(format(entry.date, dateFormat));
        }

        if (
          sinceLastEntryOnly &&
          ((entry.state === ScheduleEntryState.Complete && !!entry.value) ||
            entry.state !== ScheduleEntryState.Complete)
        ) {
          acc.lastEntryDate =
            !acc.lastEntryDate ||
            acc.lastEntryDate.getTime() < entry.date.getTime()
              ? entry.date
              : acc.lastEntryDate;
        }

        return acc;
      },
      {
        skippedDates: new Set(),
        completedDates: new Set(),
        lastEntryDate: undefined,
      },
    );

  // if there are timeframes from before the startDate (entries entered before the start of the schedule) we also take those
  const preTimeframes =
    _start.getTime() < scheduleStartDate.getTime()
      ? getScheduleTimeframesByDateRange(schedule, {
          startDate: _start,
          endDate: endOfDay(addDays(scheduleStartDate, -1)),
          weekStartsOn,
        })
      : [];
  const duringTimeframes = getScheduleTimeframesByDateRange(schedule, {
    startDate: scheduleStartDate,
    endDate: _end,
    weekStartsOn,
  });

  // get a list of all unique timeframes
  const timeframes = [
    ...new Map(
      [...preTimeframes, ...duringTimeframes].map((t) => [
        `${t.startDate.getTime()}:${t.endDate.getTime()}`,
        t,
      ]),
    ).values(),
  ];

  // loop over timeframes
  return timeframes.reduce<PlannedOccurrence[]>((acc, timeframe) => {
    const plannedOccurrences =
      schedule.plannedOccurrences
        ?.filter(({ date }) =>
          isBetween(date, timeframe.startDate, timeframe.endDate),
        )
        .sort(
          (plannedA, plannedB) =>
            plannedA.date.getTime() - plannedB.date.getTime(),
        ) ?? [];

    if (plannedOccurrences.length) {
      acc.push(
        ...plannedOccurrences.filter(
          ({ date }) =>
            !skippedDates.has(format(date, dateFormat)) &&
            !completedDates.has(format(date, dateFormat)) &&
            (!lastEntryDate || !isBefore(date, lastEntryDate)) &&
            // occurrences from before the start date are not wanted
            !isBefore(date, startDate) &&
            // occurrences after the end date are not wanted
            !isAfter(date, _end),
        ),
      );

      return acc;
    }

    // calculate endDate of timeframe (cant overflow the end date of the timeframe)
    const countEnd = endOfDay(addDays(timeframe.startDate, count - 1));
    const timeframeEnd = new Date(
      Math.min(timeframe.endDate.getTime(), countEnd.getTime()),
    );

    // if no planned occurrences, we create them with the schedule timeslot and the interval
    eachDayOfInterval({
      start: timeframe.startDate,
      end: timeframeEnd,
    }).forEach((date) => {
      if (
        !skippedDates.has(format(date, dateFormat)) &&
        !completedDates.has(format(date, dateFormat)) &&
        (!lastEntryDate || !isBefore(date, lastEntryDate)) &&
        // occurrences from before the start date are not wanted
        !isBefore(date, startDate) &&
        // occurrences after the end date are not wanted
        !isAfter(date, _end)
      ) {
        acc.push({ id: uuidv4(), date, timeslot: schedule.timeslot ?? null });
      }
    });

    return acc;
  }, []);
};
